import Select from "@/v2-ui/select"
import Tooltip from "@/v2-ui/tooltip"
import cn from "@/v2-ui/utils/utils.cn"
import {
  ACCESS_MODE_LABEL,
  describeAccessMode,
} from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.accessMode.constants"
import { useFeatureGroupLens } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.context"
import { isClosedForLens } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.lens.utils"

export type LicenseFeatureGroupAccessModeSelectProps = {
  /** The class the control decides: a content class's feature type, or the capabilities'. */
  featureTypeCode: number
  /** "map layers", "base maps", "Fusion capabilities" — for the labels. */
  subject: string
  className?: string
}

const OPEN = "open"
const LISTED = "listed"

// The one new control the customer lens adds to the package editor: per
// class, is this customer Open — everything free for everyone plus what
// their licenses include, new things arriving on their own — or do they get
// ONLY what is listed below (their licenses and the ticks here are the
// whole list; a new thing reaches them only through a ticked header rule)?
// Each option carries its sentence in the open list, where the choice is
// made (a tooltip over the trigger sat on top of the open list). It flips
// the lens's draft, so the rows below re-read at once — closed, every item
// is gated for this customer and its checkbox decides — and Save writes the
// mode with the list in one transaction. Renders nothing through the
// package lens.
function LicenseFeatureGroupAccessModeSelect(
  props: LicenseFeatureGroupAccessModeSelectProps,
) {
  const { featureTypeCode, subject, className } = props
  const lens = useFeatureGroupLens()
  if (lens.kind !== "customer") return null

  const isClosed = isClosedForLens(lens, featureTypeCode)
  const value = isClosed ? LISTED : OPEN

  // Through the person lens the mode is the customer's to set, not this
  // view's: it reads as a fact, with the sentence on hover.
  if (lens.isReadOnly) {
    return (
      <Tooltip>
        <Tooltip.Trigger asChild>
          <span
            className={cn(
              "shrink-0 cursor-help text-xs text-muted-foreground underline decoration-dotted underline-offset-2",
              className,
            )}
          >
            {isClosed ? ACCESS_MODE_LABEL.listed : ACCESS_MODE_LABEL.open}
          </span>
        </Tooltip.Trigger>
        <Tooltip.Content side="top" className="max-w-[340px]">
          {describeAccessMode(isClosed, subject)} Set for {lens.customerName} on
          the customer panel&apos;s What they get tab.
        </Tooltip.Content>
      </Tooltip>
    )
  }

  return (
    <div className={className}>
      <Select
        value={value}
        onValueChange={(next) => {
          lens.setClosed(featureTypeCode, next === LISTED)
        }}
      >
        <Select.Trigger
          size="xs"
          className="w-48"
          aria-label={`What ${lens.customerName} gets of ${subject}`}
          data-feature-code="UI_BackofficeCustomer_FeatureAccess_Mode"
        >
          <Select.Value />
        </Select.Trigger>
        <Select.Content align="end" className="max-w-[360px]">
          <Select.Item
            value={OPEN}
            description={describeAccessMode(false, subject)}
          >
            {ACCESS_MODE_LABEL.open}
          </Select.Item>
          <Select.Item
            value={LISTED}
            description={describeAccessMode(true, subject)}
          >
            {ACCESS_MODE_LABEL.listed}
          </Select.Item>
        </Select.Content>
      </Select>
    </div>
  )
}

export default LicenseFeatureGroupAccessModeSelect
